/**
 * Reverse-chronological alerts feed (spec §10). Read-only: alert rows are
 * written by the evaluator and expire on their own 30-day TTL; there is no
 * server-side ack, mute, or snooze in v1.
 *
 * Rendering the feed marks everything in it as seen (a localStorage watermark),
 * which is what clears the tab-bar unread badge.
 */

import { useEffect } from 'react';
import { useAlerts, type AlertView, type SilenceBasis } from '../lib/queries';
import { newestFiredAt, writeLastSeen } from '../lib/alerts-unread';

/**
 * Cost renders as USD; token and turn counts render grouped; silence renders in
 * MINUTES with the unit — a cadence alert shows minutes against minutes, and an
 * unlabelled `2880 ≥ 1440` beside `$12.47 ≥ $10.00` invites reading it as
 * anything.
 *
 * Exhaustive `switch`, not the `metric === 'cost_usd' ? … : …` it replaces: a
 * widened `BudgetMetric` is absorbed by the else-arm with no type error, which
 * is precisely how the server's own webhook summary once printed
 * `(period undefined undefined)`. A fifth metric fails to compile here.
 */
function fmtValue(metric: AlertView['metric'], n: number): string {
  switch (metric) {
    case 'cost_usd':
      return `$${n.toFixed(2)}`;
    case 'total_tokens':
    case 'turn_count':
      return n.toLocaleString();
    case 'silence_minutes':
      return `${n.toLocaleString()} min`;
    default: {
      const exhaustive: never = metric;
      return String(exhaustive);
    }
  }
}

/**
 * `beyond_window` — and ONLY `beyond_window` — prefixes the observed value with
 * "at least", because there the last data point (if there ever was one) is
 * older than anything the evaluator can still see: the number is the FLOOR on
 * the outage, not its length. This is the same rule, in the same words, as
 * `silenceQualifier` in `lib/lambdas/shared/webhook.ts`; the console must not
 * state as measured what the webhook calls a lower bound.
 *
 * `rule_created_at` is also a lower bound on the outage, but the row renders
 * `since <silence_since>` and the literal basis beside it, which say exactly
 * what the number is a measurement OF — silence since this rule began watching
 * — so an "at least" there would blur a figure that is exact for its claim.
 *
 * The `never` guard is load-bearing: nothing else in this file switches on the
 * basis, so without it a fourth `SilenceBasis` would silently inherit the
 * unqualified wording.
 */
function silenceQualifier(basis: SilenceBasis | undefined): string {
  switch (basis) {
    case 'beyond_window':
      return 'at least ';
    case 'last_seen':
    case 'rule_created_at':
    case undefined:
      return '';
    default: {
      const exhaustive: never = basis;
      return String(exhaustive) && '';
    }
  }
}

/**
 * What the alert fired ABOUT, as one cell. A cadence alert has no
 * `producer`/`session_id` and no `period`/`period_key` (spec §4), so the
 * `kind === 'run' ? … : period …` ternary this replaces rendered
 * `undefined undefined` for it — the exact defect the webhook's own cadence
 * branch exists to fix, reached from the console side. Exhaustive, so a fourth
 * kind cannot inherit an arm that happens to be last.
 *
 * The basis is rendered as the LITERAL token the row stores, not paraphrased:
 * `observed_value` means three materially different things depending on it, and
 * a paraphrase would drift from the field. Each missing field falls back to a
 * WORD rather than being dropped, so the cell is never bare and never
 * `undefined`.
 *
 * A run alert names the run by its address, `producer / session_id`. A run
 * alert written before the producer re-key carries no `producer` (it stored the
 * retired `platform`, which this feed does not read: it is not an address any
 * more), so its producer renders as an em-dash.
 */
function fmtTarget(a: AlertView): string {
  switch (a.kind) {
    case 'run':
      return `${a.producer ?? '—'} / ${a.session_id}`;
    case 'period':
      return `${a.period} ${a.period_key}`;
    case 'cadence':
      return `since ${a.silence_since ?? 'unknown'} · basis ${a.silence_basis ?? 'unrecorded'}`;
    default: {
      const exhaustive: never = a.kind;
      return String(exhaustive);
    }
  }
}

/**
 * `pending` is its OWN state, never folded into `none`. The evaluator writes
 * `pending` before it POSTs and overwrites it with `ok`/`failed` afterwards, so a
 * row still reading `pending` means the invocation died mid-delivery. Rendering
 * that as "no webhook" would turn a FAILED notification into an invisible one.
 */
const DELIVERY_LABEL: Record<AlertView['delivery'], string> = {
  ok: 'delivered',
  none: 'no webhook',
  pending: 'delivery pending',
  failed: 'delivery failed',
};

export default function AlertsFeed() {
  const { data, isLoading, isError, error, hasNextPage, fetchNextPage, isFetchingNextPage } = useAlerts();

  const alerts = (data?.pages ?? []).flatMap((p) => p.alerts);
  // Pages arrive already sorted server-side, but a re-sort here keeps the
  // ordering correct regardless of page-arrival order.
  const sorted = alerts.slice().sort((a, b) => (a.fired_at < b.fired_at ? 1 : a.fired_at > b.fired_at ? -1 : 0));
  const newest = newestFiredAt(alerts);

  useEffect(() => {
    writeLastSeen(newest);
  }, [newest]);

  return (
    <section className="alerts-feed" aria-label="Alerts">
      <h3 className="section-title">Alerts</h3>
      {isLoading ? (
        <p className="note">Loading alerts…</p>
      ) : isError ? (
        <p className="error" role="alert">
          Failed to load alerts: {error instanceof Error ? error.message : 'unknown error'}
        </p>
      ) : sorted.length === 0 ? (
        <p className="note">No alerts have fired in the last 30 days.</p>
      ) : (
        <>
          <ul className="alert-list">
            {sorted.map((a) => (
              <li key={a.sk} className="alert-row">
                <span className="alert-ts mono">{a.fired_at}</span>
                <span className="alert-rule">{a.rule_name}</span>
                <span className="alert-value mono">
                  {silenceQualifier(a.kind === 'cadence' ? a.silence_basis : undefined)}
                  {fmtValue(a.metric, a.observed_value)} ≥ {fmtValue(a.metric, a.threshold)}
                </span>
                <span className="alert-target mono">{fmtTarget(a)}</span>
                {a.scope_producer && <span className="pill">{a.scope_producer}</span>}
                <span className={`alert-delivery alert-delivery-${a.delivery}`}>
                  {a.delivery === 'failed' && a.delivery_error
                    ? `${DELIVERY_LABEL.failed}: ${a.delivery_error}`
                    : DELIVERY_LABEL[a.delivery]}
                </span>
              </li>
            ))}
          </ul>
          {hasNextPage && (
            <button type="button" className="btn" disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
              Load more
            </button>
          )}
        </>
      )}
    </section>
  );
}
